import type { MarkerSheetCell, MarkerSheetData } from '../core/types';

export const MAX_NOTE_SHEET_ROWS = 100;
export const MAX_NOTE_SHEET_COLUMNS = 40;

function cleanCellText(value: string): string {
  return value.replace(/\u00a0/g, ' ').replace(/\r\n?/g, '\n').trim();
}

function safeSpan(value: number): number | undefined {
  return Number.isInteger(value) && value > 1 ? Math.min(value, MAX_NOTE_SHEET_COLUMNS) : undefined;
}

function clipboardClassStyles(doc: Document): Map<string, string> {
  const styles = new Map<string, string>();
  for (const element of Array.from(doc.querySelectorAll('style'))) {
    for (const rule of (element.textContent ?? '').matchAll(/([^{}]+)\{([^{}]*)\}/g)) {
      for (const className of rule[1].matchAll(/\.([\w-]+)/g)) {
        styles.set(className[1], `${styles.get(className[1]) ?? ''};${rule[2]}`);
      }
    }
  }
  return styles;
}

function classStyle(cell: HTMLTableCellElement, classStyles: Map<string, string>): string {
  return Array.from(cell.classList)
    .map((name) => classStyles.get(name) ?? '')
    .join(';');
}

function cellAlignment(cell: HTMLTableCellElement, copiedClassStyle: string): MarkerSheetCell['align'] {
  const styledAlignment = `${cell.getAttribute('style') ?? ''};${copiedClassStyle}`.match(/text-align\s*:\s*(left|center|right)/i)?.[1];
  const value = (cell.style.textAlign || cell.getAttribute('align') || styledAlignment || '').toLowerCase();
  return value === 'center' || value === 'right' ? value : value === 'left' ? 'left' : undefined;
}

/** Converts clipboard data from Google Sheets (HTML when available, TSV as
 * a universal fallback) into the small, sanitized grid stored by the app. */
export function parseSheetClipboard(html: string, plainText: string): MarkerSheetData {
  if (html.trim()) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const table = doc.querySelector('table');
    if (table) {
      const classStyles = clipboardClassStyles(doc);
      const rows = Array.from(table.rows)
        .slice(0, MAX_NOTE_SHEET_ROWS)
        .map((row) =>
          Array.from(row.cells)
            .slice(0, MAX_NOTE_SHEET_COLUMNS)
            .map((cell) => {
              const copiedClassStyle = classStyle(cell, classStyles);
              return {
                text: cleanCellText(cell.textContent ?? ''),
                colSpan: safeSpan(cell.colSpan),
                rowSpan: safeSpan(cell.rowSpan),
                bold:
                  cell.querySelector('b, strong') !== null ||
                  /^(bold|[6-9]00)$/i.test(cell.style.fontWeight) ||
                  /font-weight\s*:\s*(bold|[6-9]00)/i.test(`${cell.getAttribute('style') ?? ''};${copiedClassStyle}`) ||
                  undefined,
                align: cellAlignment(cell, copiedClassStyle),
              };
            }),
        );
      if (rows.length > 0) return { rows };
    }
  }

  const normalized = plainText.replace(/\r\n?/g, '\n').replace(/\n$/, '');
  if (!normalized) return { rows: [] };
  return {
    rows: normalized
      .split('\n')
      .slice(0, MAX_NOTE_SHEET_ROWS)
      .map((row) =>
        row
          .split('\t')
          .slice(0, MAX_NOTE_SHEET_COLUMNS)
          .map((text) => ({ text: cleanCellText(text) })),
      ),
  };
}

export function sheetClipboardExceedsLimits(html: string, plainText: string): boolean {
  if (html.trim()) {
    const table = new DOMParser().parseFromString(html, 'text/html').querySelector('table');
    if (table) {
      return (
        table.rows.length > MAX_NOTE_SHEET_ROWS ||
        Array.from(table.rows).some(
          (row) => Array.from(row.cells).reduce((total, cell) => total + Math.max(1, cell.colSpan), 0) > MAX_NOTE_SHEET_COLUMNS,
        )
      );
    }
  }
  const rows = plainText.replace(/\r\n?/g, '\n').split('\n');
  return rows.length > MAX_NOTE_SHEET_ROWS || rows.some((row) => row.split('\t').length > MAX_NOTE_SHEET_COLUMNS);
}

export function sheetHasContent(sheet: MarkerSheetData | undefined): boolean {
  return Boolean(sheet?.rows.some((row) => row.some((cell) => cell.text.trim())));
}

export function sheetToPlainText(sheet: MarkerSheetData | undefined): string {
  return sheet?.rows.map((row) => row.map((cell) => cell.text).join('\t')).join('\n') ?? '';
}

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

export function renderSheetTable(sheet: MarkerSheetData, editable: boolean): string {
  if (sheet.rows.length === 0) return '';
  return `<table class="note-sheet-table"><tbody>${sheet.rows
    .map(
      (row, rowIndex) =>
        `<tr>${row
          .map((cell, cellIndex) => {
            const attrs = [
              cell.colSpan ? `colspan="${cell.colSpan}"` : '',
              cell.rowSpan ? `rowspan="${cell.rowSpan}"` : '',
              cell.bold ? 'class="is-bold"' : '',
              cell.align ? `data-align="${cell.align}"` : '',
              editable ? 'contenteditable="true" spellcheck="false"' : '',
              editable ? `data-sheet-row="${rowIndex}" data-sheet-cell="${cellIndex}"` : '',
            ]
              .filter(Boolean)
              .join(' ');
            return `<td ${attrs}>${escapeHtml(cell.text)}</td>`;
          })
          .join('')}</tr>`,
    )
    .join('')}</tbody></table>`;
}
